import { markStale } from '../resolvers/definition/resolverState';
import { produceNextCacheEntry } from './lifecycleTransition';
import { createDirectedGraph } from 'directed-graph';
import { serializeCacheEntry } from '../devtools/messages';
export const createEntityLifecycleManager = ({
  logger,
  onInvalidate,
  onStateChange
}) => {
  logger.debug('startup');
  const cache = createDirectedGraph();
  const get = (__entity, instanceKey) => cache.node.get(instanceKey);
  const set = (entity, instanceKey, entry) => {
    logger.debug('setting cache entry', {
      typeName: entity.typeName,
      instanceKey
    });
    cache.node.set(instanceKey, entry);
    onStateChange(entity, instanceKey, entry.state);
  };
  return {
    // Bounce back to controller for now — will trigger invalidation
    // propagation in the future
    invalidate: (entity, instanceKey) => {
      logger.info('invalidating', {
        typeName: entity.typeName,
        instanceKey
      });
      onInvalidate(entity, instanceKey);
    },
    getState: (entity, instanceKey) => {
      var _get;
      return (_get = get(entity, instanceKey)) === null || _get === void 0 ? void 0 : _get.state;
    },
    markStale: (entity, instanceKey) => {
      const staleLogger = logger.withTags({
        typeName: entity.typeName,
        instanceKey
      });
      const entry = get(entity, instanceKey);
      if (entry && !entry.state.stale) {
        staleLogger.debug('attempting to mark stale');
        const nextEntry = produceNextCacheEntry({
          entity,
          instanceKey,
          currentEntry: entry,
          nextState: markStale()(entry.state)
        });

        // Only update + emit if the node became stale
        if (nextEntry !== null && nextEntry !== void 0 && nextEntry.state.stale) {
          staleLogger.info('marking stale');
          set(entity, instanceKey, nextEntry);
        } else {
          staleLogger.debug('did not transition to stale');
        }
      }
    },
    handleStateChange: (entity, instanceKey, nextState) => {
      const currentEntry = get(entity, instanceKey);
      const stateChangeLogger = logger.withTags({
        typeName: entity.typeName,
        instanceKey
      });
      stateChangeLogger.debug('attempting to process state change');
      const nextEntry = produceNextCacheEntry({
        entity,
        instanceKey,
        currentEntry,
        nextState
      });
      if (nextEntry !== currentEntry) {
        stateChangeLogger.info('processing state change');
        set(entity, instanceKey, nextEntry);
      } else {
        logger.debug('duplicate state, will not process update');
      }
    },
    __devtools: {
      cache: {
        getAllEntries: () => {
          const entries = {};
          for (const [instanceKey, entry] of cache.node.entries()) {
            entries[instanceKey] = {
              typeName: entry.typeName,
              instanceKey,
              entry: serializeCacheEntry(entry)
            };
          }
          return entries;
        },
        getSingleEntry: (typeName, instanceKey) => {
          const entry = cache.node.get(instanceKey);
          if (!entry) {
            throw new Error(`Entry not found for type name: ${typeName} and instance key: ${instanceKey}`);
          }
          return {
            typeName,
            instanceKey,
            entry: serializeCacheEntry(entry)
          };
        }
      }
    }
  };
};